import { createSignal, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { DeniedState } from '../components/DeniedState';
import { EmptyState } from '../components/EmptyState';
import { GithubRepoBindings } from '../components/GithubRepoBindings';
import { GithubTokenCard } from '../components/GithubTokenCard';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { t } from '../i18n';
import {
  connectGithubToken,
  disconnectGithubToken,
  parseRepos,
  useGithubTokenConnection,
} from '../lib/github-token';
import { createMutation } from '../lib/mutation';
import { bindRepo, unbindRepo, useRepoBindings } from '../lib/repo-bindings';
import { SESSION_TOKEN, useSessionMe } from '../lib/session';

// Container for the GitHub user-token connect card (#21). Owns the read +
// connect/disconnect mutations over `/v1/me/github-token`; `GithubTokenCard`
// renders. `token` is injectable for tests (mirrors MyKeys / AccountSso); the
// router mounts it with no props → the dev session.
export function GithubToken(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const hasSession = token !== null;
  // `/v1/me` publishes the token route's own SSO-pin gate (#7426): a pinned session
  // is refused there, so it never asks. Until `/v1/me` answers the read waits; a
  // `/v1/me` that failed or said nothing asks anyway, and its 403 is the answer.
  const me = useSessionMe(token);
  const pinned = () => me.data?.sso_pinned === true;
  const query = useGithubTokenConnection(token, () => (me.isSuccess || me.isError) && !pinned());

  const [confirming, setConfirming] = createSignal(false);

  const connect = createMutation({
    call: (input: { token: string; repos: string }) =>
      connectGithubToken({ token: input.token, repos: parseRepos(input.repos) }, token),
    fallback: t('github_token.connect_error'),
    onSettled: () => query.refetch(),
  });

  // A destructive action never no-ops silently — a refusal keeps the confirm
  // open (only `onSuccess` closes it) and renders the API's own sentence.
  const disconnect = createMutation({
    call: () => disconnectGithubToken(token),
    fallback: t('github_token.disconnect_error'),
    onSuccess: () => setConfirming(false),
    onSettled: () => query.refetch(),
  });

  return (
    <>
      <PageHeader title={t('github_token.title')} desc={t('github_token.subtitle')} />
      <Show when={hasSession} fallback={<EmptyState message={t('github_token.no_session')} />}>
        <Show when={!pinned()} fallback={<DeniedState message={t('github_token.sso_pinned')} />}>
          <QueryBoundary
            query={query}
            loading={<CardGridSkeleton listClass="github-token__loading" count={1} />}
          >
            {(data) => (
              <>
                <GithubTokenCard
                  connection={data.connection}
                  connectBusy={connect.pending()}
                  connectError={connect.error()}
                  onConnect={(input) => void connect.run(input)}
                  confirming={confirming()}
                  disconnectBusy={disconnect.pending()}
                  disconnectError={disconnect.error()}
                  onAskDisconnect={() => {
                    disconnect.clearError();
                    setConfirming(true);
                  }}
                  onCancelDisconnect={() => setConfirming(false)}
                  onConfirmDisconnect={() => void disconnect.run()}
                />
                <Show when={data.connection}>
                  {(conn) => <RepoBindings repos={conn().repos} token={token} />}
                </Show>
              </>
            )}
          </QueryBoundary>
        </Show>
      </Show>
    </>
  );
}

// Container for the bind/unbind control under a connected token (arc 101 slice
// 05): reads `/v1/me/repo-bindings`, runs one bind or unbind at a time, and hands
// the API's refusal sentence down. `GithubRepoBindings` renders.
function RepoBindings(props: { repos: readonly string[]; token: string | null }): JSX.Element {
  const query = useRepoBindings(props.token);
  const [busyRepo, setBusyRepo] = createSignal<string | null>(null);
  const toggle = createMutation({
    call: async (input: { repo: string; bind: boolean }) => {
      if (input.bind) await bindRepo(input.repo, props.token);
      else await unbindRepo(input.repo, props.token);
    },
    fallback: t('github_token.binding_error'),
    onSettled: async () => {
      setBusyRepo(null);
      await query.refetch();
    },
  });
  const run = (repo: string, bind: boolean) => {
    setBusyRepo(repo);
    void toggle.run({ repo, bind });
  };
  return (
    <QueryBoundary
      query={query}
      loading={<CardGridSkeleton listClass="github-token__loading" count={1} />}
    >
      {(data) => (
        <GithubRepoBindings
          repos={props.repos}
          bindings={data.bindings}
          busyRepo={busyRepo()}
          error={toggle.error()}
          onBind={(repo) => run(repo, true)}
          onUnbind={(repo) => run(repo, false)}
        />
      )}
    </QueryBoundary>
  );
}
